"use client";

import { authClient } from "@repo/auth/client";
import { config } from "@repo/config";
import { Button } from "@ui/components/button";
import { useSearchParams } from "next/navigation";
import { useQueryState } from "nuqs";
import { parseAsString } from "nuqs";
import { Suspense } from "react";
import { oAuthProviders } from "../constants/oauth-providers";

// 内部组件包含 useSearchParams 调用
function SocialSigninButtonInner({
	provider,
	className,
	redirectProp,
}: {
	provider: keyof typeof oAuthProviders;
	className?: string;
	redirectProp?: string;
}) {
	const [invitationId] = useQueryState("invitationId", parseAsString);
	const searchParams = useSearchParams();
	const redirectToParam = searchParams.get("redirectTo");
	const providerData = oAuthProviders[provider];

	// 检查是否有通过模态窗口传递的重定向路径（保存在localStorage中）
	let storedRedirectPath: string | null = null;
	if (typeof window !== "undefined") {
		try {
			storedRedirectPath = localStorage.getItem("modalRedirectPath");
			if (storedRedirectPath) {
				console.log("Found stored redirect path:", storedRedirectPath);
			}
		} catch (e) {
			console.error("Error accessing localStorage:", e);
		}
	}

	// 优先级顺序：redirectProp > storedRedirectPath > URL参数 > 默认路径
	const redirectPath = invitationId
		? `/app/organization-invitation/${invitationId}`
		: (redirectProp ??
			storedRedirectPath ??
			redirectToParam ??
			config.auth.redirectAfterSignIn);

	console.log(
		"Social login redirect path:",
		redirectPath,
		"Provider:",
		provider,
	);

	const onSignin = () => {
		// 将重定向路径保存到localStorage，防止在OAuth重定向过程中丢失
		if (typeof window !== "undefined" && redirectPath) {
			try {
				localStorage.setItem("socialLoginRedirect", redirectPath);
				console.log(
					"Saved redirect path to localStorage:",
					redirectPath,
				);
			} catch (e) {
				console.error("Error saving to localStorage:", e);
			}
		}

		const callbackURL = new URL(redirectPath, window.location.origin);
		authClient.signIn.social({
			provider,
			callbackURL: callbackURL.toString(),
		});
	};

	return (
		<Button
			onClick={() => onSignin()}
			variant="outline"
			type="button"
			className={className}
		>
			{providerData.icon && (
				<i className="mr-2 text-primary">
					<providerData.icon className="size-4" />
				</i>
			)}
			{providerData.name}
		</Button>
	);
}

// 导出组件包含 Suspense 边界
export function SocialSigninButton({
	provider,
	className,
	redirectTo,
}: {
	provider: keyof typeof oAuthProviders;
	className?: string;
	redirectTo?: string;
}) {
	return (
		<Suspense
			fallback={
				<Button variant="outline" className={className} disabled>
					Loading...
				</Button>
			}
		>
			<SocialSigninButtonInner
				provider={provider}
				className={className}
				redirectProp={redirectTo}
			/>
		</Suspense>
	);
}
